import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MENTOR_PASSWORD_SKIP_LIMIT } from "../../../utils/constants";
import { API } from "../../../Services/api-services";

export const useOtpResetPopup = (profileRes) => {
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [otpState, setOtpState] = useState({
    currentStep: "verification", // verification | otp | password | success
    otp: "",
    newPassword: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
    redirectTimer: 5,
    otpLoading: false,
    error: "",
    isInvalidOtp: false,
    attemptsLeft: 3,
    resendTimer: 59,
    canResendOtp: true,
    isResendMode: false,
  });
  const {
    currentStep,
    otp,
    newPassword,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    redirectTimer,
    loading: otpLoading,
    error,
    isInvalidOtp,
    attemptsLeft,
    resendTimer,
    canResendOtp,
    isResendMode,
  } = otpState;
  const [canSkip, setCanSkip] = useState(false);
  const [skipsRemaining, setSkipsRemaining] = useState(
    MENTOR_PASSWORD_SKIP_LIMIT,
  );

  const isPasswordValid =
    otpState.newPassword.trim() !== ""
      ? /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(
        otpState.newPassword,
      )
      : true;

  const [emailInput, setEmailInput] = useState("");

  // Seed state from profile API response
  useEffect(() => {
    console.log("Profile Response in OTP Popup:", profileRes);
    if (!profileRes) return;
    setEmailInput(profileRes.email || "");
    setCanSkip(!!profileRes.canSkip);
    setSkipsRemaining(MENTOR_PASSWORD_SKIP_LIMIT - (profileRes.passwordResetSkipCount ?? 0));
    if (!profileRes.isPasswordResetDone) {
      setOtpState((prev) => ({ ...prev, currentStep: "verification" }));
      setOpenOtpModal(true);
    } else {
      localStorage.setItem("isPasswordResetDone", "1");
    }
  }, [profileRes]);

  useEffect(() => {
    let interval;

    if (otpState.isResendMode && otpState.resendTimer > 0) {
      interval = setInterval(() => {
        setOtpState((prev) => ({
          ...prev,
          resendTimer: prev.resendTimer - 1,
        }));
      }, 1000);
    }

    if (otpState.resendTimer === 0) {
      setOtpState((prev) => ({
        ...prev,
        canResendOtp: true,
        isResendMode: false,
      }));
    }

    if (otpState.currentStep === "success" && otpState.redirectTimer > 0) {
      interval = setInterval(() => {
        setOtpState((prev) => ({
          ...prev,
          redirectTimer: prev.redirectTimer - 1,
        }));
      }, 1000);
    }

    if (otpState.currentStep === "success" && otpState.redirectTimer === 0) {
      localStorage.removeItem("user");
      localStorage.removeItem("workShift");
      window.location.replace("/");
    }

    return () => clearInterval(interval);
  }, [
    otpState.isResendMode,
    otpState.resendTimer,
    otpState.currentStep,
    otpState.redirectTimer,
  ]);


  const handleSendOtp = async (isResend = false) => {
    try {
      setOtpState((prev) => ({
        ...prev,
        otpLoading: true,
        otp: "",
        currentStep: "otp",
        ...(isResend
          ? {}
          : { resendTimer: 59, canResendOtp: true, isResendMode: false }),
        error: "",
      }));

      await API.post("/sendEmailOTP", {
        email: emailInput,
      });
    } catch (e) {
      setOtpState((prev) => ({
        ...prev,
        error: "Failed to send verification code.",
      }));
    } finally {
      setOtpState((prev) => ({
        ...prev,
        otpLoading: false,
      }));
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setOtpState((prev) => ({
        ...prev,
        otpLoading: true,
        error: "",
      }));

      const res = await API.post("/verifyEmailOtp", {
        email: emailInput,
        emailOTP: otp,
      });

      if (res?.data?.otpVerified) {
        setOtpState((prev) => ({
          ...prev,
          isInvalidOtp: false,
          currentStep: "password",
        }));
      } else {
        setOtpState((prev) => ({
          ...prev,
          attemptsLeft: prev.attemptsLeft - 1,
          isInvalidOtp: true,
          error: "Invalid verification code.",
        }));
      }
    } catch (e) {
      setOtpState((prev) => ({
        ...prev,
        attemptsLeft: prev.attemptsLeft - 1,
        isInvalidOtp: true,
        // error: "Verification failed.",
      }));
    } finally {
      setOtpState((prev) => ({
        ...prev,
        otpLoading: false,
      }));
    }
  };

  const handleSkip = async () => {
    try {
      const res = await API.patch("/mentor/skipPasswordReset");
      const remaining = res?.data?.skipsRemaining ?? skipsRemaining - 1;
      setSkipsRemaining(remaining);
      setCanSkip(remaining > 0);
      setOpenOtpModal(false);
    } catch (e) {
      toast.error(
        "Password Reset Skip Attempt Limit Reached. Please reset your password.",
      );
    }
  };

  const handleSetNewPassword = async () => {
    if (
      !isPasswordValid ||
      !newPassword ||
      !confirmPassword ||
      newPassword !== confirmPassword
    )
      return;

    try {
      setOtpState((prev) => ({
        ...prev,
        otpLoading: true,
        error: "",
      }));

      await API.post("/setNewPassword", {
        email: emailInput,
        password: newPassword,
        isMentorPasswordResetFlow: true,
      });

      setOtpState((prev) => ({
        ...prev,
        currentStep: "success",
        redirectTimer: 5,
      }));
    } catch (e) {
      setOtpState((prev) => ({
        ...prev,
        error: "Failed to set new password. Please try again.",
      }));
    } finally {
      setOtpState((prev) => ({
        ...prev,
        otpLoading: false,
      }));
    }
  };

  return {
    openOtpModal,
    otpState,
    setOtpState,
    emailInput,
    handleSendOtp,
    handleVerifyOtp,
    handleSetNewPassword,
    isPasswordValid,
    setOpenOtpModal,
    canSkip,
    skipsRemaining,
    handleSkip,
  }

};